import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Tooltip } from './ui';
import { cn } from '../lib/cn';

/**
 * SidebarGroup — Sprint 28c collapsible nav grup.
 *
 * BRANDBOOK §5.1 (O4C) — İkili nav hiyerarşisi.
 *   - Grup başlığı: ikon + label + chevron, tıklanınca aç/kapan
 *   - Aktif alt sayfa varsa otomatik açık (location.pathname eşleşme)
 *   - Persist: `localStorage['custel-sidebar-group-{groupId}']`
 *   - Sidebar collapsed (64px) modda: grup başlığı sadece ikon, tooltip + hover-flyout panel
 *   - Açılma 200ms ease (BRANDBOOK §8.1 standard easing)
 *
 * Tasarım: ink bg üzerinde g300 başlık (hover krem). Açık state: krem başlık + chevron 180°.
 * Alt öğeler: indented (sol 8px), 3px lime stripe aktif item için.
 */

export interface SidebarGroupItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  badge?: ReactNode;
}

interface SidebarGroupProps {
  /** Stable id — localStorage key için. */
  id: string;
  label: string;
  icon: LucideIcon;
  items: SidebarGroupItem[];
  /** Sidebar collapsed (64px) — flyout panel davranışı. */
  collapsed: boolean;
}

function storageKey(id: string) {
  return `custel-sidebar-group-${id}`;
}

function loadOpen(id: string, fallback: boolean): boolean {
  if (typeof window === 'undefined') return fallback;
  try {
    const v = window.localStorage.getItem(storageKey(id));
    if (v === '1') return true;
    if (v === '0') return false;
  } catch {
    /* private mode */
  }
  return fallback;
}

export function SidebarGroup({ id, label, icon: Icon, items, collapsed }: SidebarGroupProps) {
  const { t } = useTranslation('components');
  const location = useLocation();

  // Aktif alt sayfa kontrolü — group otomatik açık
  const hasActiveChild = items.some(
    (it) => location.pathname === it.to || (!it.end && location.pathname.startsWith(it.to + '/')),
  );

  const [open, setOpen] = useState<boolean>(() => loadOpen(id, hasActiveChild));
  // Aktif çocuk değişirse aç (kullanıcı kapatmışsa bile route gidişinde mantıklı UX)
  useEffect(() => {
    if (hasActiveChild) setOpen(true);
  }, [hasActiveChild]);

  // Persist
  useEffect(() => {
    try {
      window.localStorage.setItem(storageKey(id), open ? '1' : '0');
    } catch {
      /* ignore */
    }
  }, [id, open]);

  // Collapsed modda flyout için referans
  const [flyoutOpen, setFlyoutOpen] = useState(false);
  const closeTimer = useRef<number | null>(null);

  function scheduleClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setFlyoutOpen(false), 120);
  }
  function cancelClose() {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }

  // Sidebar collapsed (64px): grup başlığı = sadece ikon, hover'da flyout panel açılır
  if (collapsed) {
    return (
      <div
        className="relative"
        onMouseEnter={() => {
          cancelClose();
          setFlyoutOpen(true);
        }}
        onMouseLeave={scheduleClose}
      >
        <Tooltip content={label}>
          <button
            type="button"
            aria-haspopup="menu"
            aria-expanded={flyoutOpen}
            className={cn(
              'group relative flex w-full items-center justify-center rounded-r-custel-md px-2 py-2 text-custel-g300 transition-colors duration-custel-fast hover:bg-white/[0.04] hover:text-custel-krem focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-lime',
              hasActiveChild && 'text-custel-krem',
            )}
          >
            {hasActiveChild && (
              <span aria-hidden="true" className="absolute inset-y-1.5 left-0 w-[3px] rounded-r bg-custel-lime" />
            )}
            <Icon size={16} aria-hidden="true" />
          </button>
        </Tooltip>

        {flyoutOpen && (
          <div
            role="menu"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            className="absolute left-full top-0 z-50 ml-2 min-w-[200px] rounded-custel-md border border-white/[0.08] bg-custel-ink py-1 shadow-custel-lg"
          >
            <p className="px-3 pb-1 pt-2 font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-custel-g500">
              {label}
            </p>
            {items.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                role="menuitem"
                className={({ isActive }) =>
                  cn(
                    'flex items-center gap-2.5 px-3 py-1.5 text-[13px] text-custel-g300 transition-colors hover:bg-white/[0.06] hover:text-custel-krem focus:outline-none focus-visible:bg-white/[0.06]',
                    isActive && 'bg-custel-lime/[0.12] font-medium text-custel-krem',
                  )
                }
              >
                <it.icon size={14} aria-hidden="true" />
                <span className="flex-1 truncate">{it.label}</span>
                {it.badge}
              </NavLink>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Expanded sidebar — accordion açılma/kapanma
  const contentId = `sidebar-group-content-${id}`;
  return (
    <div className="select-none">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={contentId}
        className={cn(
          'group flex w-full items-center gap-2.5 rounded-r-custel-md px-3 py-2 text-sm transition-colors duration-custel-fast focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-lime',
          hasActiveChild ? 'text-custel-krem' : 'text-custel-g300 hover:bg-white/[0.04] hover:text-custel-krem',
        )}
      >
        <Icon size={16} aria-hidden="true" />
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronDown
          size={14}
          aria-hidden="true"
          className={cn(
            'shrink-0 text-custel-g500 transition-transform duration-custel-fast',
            open && 'rotate-180',
            hasActiveChild && 'text-custel-g300',
          )}
        />
      </button>

      {/* Accordion içerik — overflow hidden + max-height transition */}
      <div
        id={contentId}
        role="region"
        aria-label={t('sidebarGroup.submenuLabel', { label })}
        className={cn(
          'overflow-hidden transition-[max-height,opacity] duration-custel-base ease-[cubic-bezier(0.2,0.9,0.3,1)]',
          open ? 'max-h-[600px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <div className="ml-3 mt-0.5 space-y-0.5 border-l border-white/[0.08] pl-2 pt-0.5">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              className={({ isActive }) =>
                cn(
                  'group relative flex items-center gap-2.5 rounded-r-custel-md px-3 py-1.5 text-[13px] text-custel-g300 transition-colors duration-custel-fast hover:bg-white/[0.04] hover:text-custel-krem focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-custel-lime',
                  isActive && 'bg-custel-lime/[0.12] font-medium text-custel-krem',
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span
                      aria-hidden="true"
                      className="absolute inset-y-1 left-[-9px] w-[3px] rounded-r bg-custel-lime"
                    />
                  )}
                  <it.icon size={14} aria-hidden="true" />
                  <span className="flex-1 truncate">{it.label}</span>
                  {it.badge}
                </>
              )}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
}
